import { useState, createElement } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Plus, Pencil, Trash2, GripVertical, Upload, X, Check, BarChart3,
  ShoppingBag, Shirt, Coffee, Gift, Heart, Star, Gem, Music,
  Book, Camera, Utensils, Home, Car, Palette, Laptop, Phone,
  Headphones, Watch, Gamepad2, Pizza, Flower2, GitBranch,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { categoriesApi } from '../../api/endpoints/categories';
import { CategoryStatsModal } from './CategoryStatsModal';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { Modal } from '../../components/ui/Modal';
import { PermissionGate } from '../../components/ui/PermissionGate';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { Select } from '../../components/ui/Select';
import { Toggle } from '../../components/ui/Toggle';
import { useToast } from '../../components/ui/Alert';
import { getCdnUrl } from '../../lib/formatters';
import { useStore } from '../../contexts/StoreContext';
import type { Category, CreateCategoryInput } from '../../types/category';
import { HierarchyEditor } from './HierarchyEditor';

const ICON_LIBRARY = [
  { name: 'ShoppingBag', icon: ShoppingBag },
  { name: 'Shirt', icon: Shirt },
  { name: 'Coffee', icon: Coffee },
  { name: 'Gift', icon: Gift },
  { name: 'Heart', icon: Heart },
  { name: 'Star', icon: Star },
  { name: 'Gem', icon: Gem },
  { name: 'Music', icon: Music },
  { name: 'Book', icon: Book },
  { name: 'Camera', icon: Camera },
  { name: 'Utensils', icon: Utensils },
  { name: 'Home', icon: Home },
  { name: 'Car', icon: Car },
  { name: 'Palette', icon: Palette },
  { name: 'Laptop', icon: Laptop },
  { name: 'Phone', icon: Phone },
  { name: 'Headphones', icon: Headphones },
  { name: 'Watch', icon: Watch },
  { name: 'Gamepad2', icon: Gamepad2 },
  { name: 'Pizza', icon: Pizza },
  { name: 'Flower2', icon: Flower2 },
] as const;

function lucideToSvgString(IconComponent: LucideIcon): string {
  const tempDiv = document.createElement('div');
  const root = createRoot(tempDiv);
  flushSync(() => {
    root.render(
      createElement(IconComponent, {
        width: 24,
        height: 24,
        stroke: 'currentColor',
        strokeWidth: 2,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        fill: 'none',
      }),
    );
  });
  const svg = tempDiv.innerHTML;
  root.unmount();
  return svg;
}

export function CategoryList() {
  const { storeId } = useParams<{ storeId: string }>();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentStore } = useStore();

  const [showModal, setShowModal] = useState(false);
  const [showHierarchyEditor, setShowHierarchyEditor] = useState(false);
  const [editTarget, setEditTarget] = useState<Category | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Category | null>(null);
  const [formData, setFormData] = useState<CreateCategoryInput>({ name: '' });
  const [selectedLibraryIcon, setSelectedLibraryIcon] = useState<string | null>(null);
  const [statsCategory, setStatsCategory] = useState<Category | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['categories', storeId],
    queryFn: () => categoriesApi.list(storeId!),
    enabled: !!storeId,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateCategoryInput) => categoriesApi.create(storeId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories', storeId] });
      toast('success', t('categories.createSuccess'));
      closeModal();
    },
    onError: () => toast('error', t('common.error')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateCategoryInput> }) =>
      categoriesApi.update(storeId!, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories', storeId] });
      toast('success', t('categories.updateSuccess'));
      closeModal();
    },
    onError: () => toast('error', t('common.error')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => categoriesApi.delete(storeId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories', storeId] });
      toast('success', t('categories.deleteSuccess'));
      setDeleteTarget(null);
    },
    onError: () => toast('error', t('common.error')),
  });

  const closeModal = () => {
    setShowModal(false);
    setEditTarget(null);
    setFormData({ name: '' });
    setSelectedLibraryIcon(null);
  };

  const openEdit = (cat: Category) => {
    setEditTarget(cat);
    setFormData({
      name: cat.name,
      description: cat.description,
      parentId: cat.parentId,
      isActive: cat.isActive ?? true,
      iconImage: cat.iconImage,
      banner: cat.banner,
    });
    setShowModal(true);
  };

  const handleImageUpload = (field: 'iconImage' | 'banner') => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => setFormData(prev => ({ ...prev, [field]: reader.result as string }));
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const handleSubmit = () => {
    if (!formData.name.trim()) return;
    if (editTarget) {
      updateMutation.mutate({ id: editTarget._id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const allCategories = data?.categories ?? [];

  // Build tree: top-level categories + their children
  const topLevel = allCategories.filter(c => !c.parentId);
  const getChildren = (parentId: string) => allCategories.filter(c => c.parentId === parentId);

  if (isLoading) return <LoadingSpinner fullPage />;

  function renderCategoryCard(cat: Category, depth: number = 0) {
    const children = cat.children ?? getChildren(cat._id);
    return (
      <div key={cat._id} className={depth > 0 ? 'ms-8 mt-2' : ''}>
        <div className="group rounded-xl border border-border-default bg-bg-secondary p-4 transition-colors hover:border-accent/30">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent">
              {cat.iconImage ? (
                <img src={getCdnUrl(cat.iconImage, currentStore?._id, 'categories')} alt="" className="h-6 w-6 object-contain" />
              ) : cat.icon ? (
                <span className="h-5 w-5" dangerouslySetInnerHTML={{ __html: cat.icon }} />
              ) : (
                <GripVertical className="h-5 w-5" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-medium truncate">{cat.name}</h3>
              {cat.description && (
                <p className="text-xs text-text-muted mt-0.5 truncate">{cat.description}</p>
              )}
              {children.length > 0 && (
                <p className="text-xs text-accent mt-0.5">{children.length} {t('categories.subcategories', 'subcategories')}</p>
              )}
            </div>
            <PermissionGate requires="products.edit">
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => setStatsCategory(cat)} className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-accent" title={t('products.stats', 'Stats')}>
                  <BarChart3 className="h-4 w-4" />
                </button>
                <button onClick={() => openEdit(cat)} className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-accent">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => setDeleteTarget(cat)} className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-danger">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </PermissionGate>
          </div>
        </div>
        {children.map(child => renderCategoryCard(child, depth + 1))}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-bold">{t('sidebar.categories')}</h1>
        <PermissionGate requires="products.edit">
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => setShowHierarchyEditor(true)}>
              <GitBranch className="h-4 w-4" />
              {t('categories.manageHierarchy', 'Manage Hierarchy')}
            </Button>
            <Button onClick={() => setShowModal(true)}>
              <Plus className="h-4 w-4" />
              {t('categories.addCategory')}
            </Button>
          </div>
        </PermissionGate>
      </div>

      {allCategories.length === 0 ? (
        <EmptyState title={t('common.noData')} description={t('categories.empty')} />
      ) : (
        <div className="space-y-3">
          {topLevel.map(cat => renderCategoryCard(cat))}
        </div>
      )}

      {/* Create/Edit modal */}
      <Modal
        isOpen={showModal}
        onClose={closeModal}
        title={editTarget ? t('categories.editCategory') : t('categories.addCategory')}
        footer={
          <>
            <Button variant="secondary" onClick={closeModal}>{t('common.cancel')}</Button>
            <Button
              onClick={handleSubmit}
              isLoading={createMutation.isPending || updateMutation.isPending}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label={t('common.name')}
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
          <div>
            <Textarea
              label={t('common.description')}
              rows={2}
              maxLength={200}
              value={formData.description ?? ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            <p className="mt-1 text-end text-xs text-text-muted">
              {(formData.description ?? '').length}/200
            </p>
          </div>
          <Select
            label={t('categories.parentCategory', 'Parent Category')}
            options={[
              { value: '', label: t('categories.noParent', 'None (top-level)') },
              ...allCategories
                .filter(c => c._id !== editTarget?._id)
                .map(c => ({ value: c._id, label: c.name })),
            ]}
            value={formData.parentId ?? ''}
            onChange={(e) => setFormData({ ...formData, parentId: e.target.value || undefined })}
          />

          {/* Icon image */}
          <div>
            <label className="mb-1.5 block text-sm font-medium">{t('categories.iconImage', 'Icon Image')}</label>
            {formData.iconImage ? (
              <div className="flex items-center gap-3">
                <img
                  src={formData.iconImage.startsWith('data:') ? formData.iconImage : getCdnUrl(formData.iconImage, currentStore?._id, 'categories')}
                  alt=""
                  className="h-12 w-12 rounded-lg object-cover border border-border-default"
                />
                <button type="button" onClick={() => { setFormData({ ...formData, iconImage: undefined, icon: undefined }); setSelectedLibraryIcon(null); }} className="text-text-muted hover:text-danger">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : formData.icon && selectedLibraryIcon ? (
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-accent bg-accent/10 text-accent">
                  <span className="h-6 w-6" dangerouslySetInnerHTML={{ __html: formData.icon }} />
                </div>
                <span className="text-xs text-text-muted">{selectedLibraryIcon}</span>
                <button type="button" onClick={() => { setFormData({ ...formData, icon: undefined }); setSelectedLibraryIcon(null); }} className="text-text-muted hover:text-danger">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => handleImageUpload('iconImage')}
                className="flex items-center gap-2 rounded-lg border border-dashed border-border-default px-4 py-2.5 text-sm text-text-muted hover:border-accent/50 hover:text-accent transition-colors"
              >
                <Upload className="h-4 w-4" />
                {t('common.upload', 'Upload')}
              </button>
            )}

            {/* Icon library divider and grid */}
            {!formData.iconImage && (
              <div className="mt-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-px flex-1 bg-border-default" />
                  <span className="text-xs text-text-muted">{t('categories.chooseFromLibrary', 'Choose from Library')}</span>
                  <div className="h-px flex-1 bg-border-default" />
                </div>
                <div className="grid grid-cols-7 gap-2">
                  {ICON_LIBRARY.map(({ name, icon: Icon }) => (
                    <button
                      key={name}
                      type="button"
                      title={name}
                      onClick={() => {
                        const svgStr = lucideToSvgString(Icon);
                        setFormData({ ...formData, icon: svgStr, iconImage: undefined });
                        setSelectedLibraryIcon(name);
                      }}
                      className={`flex h-10 w-10 items-center justify-center rounded-lg border transition-colors ${
                        selectedLibraryIcon === name
                          ? 'border-accent bg-accent/15 text-accent'
                          : 'border-border-default text-text-muted hover:border-accent/50 hover:text-accent hover:bg-bg-hover'
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Banner image */}
          <div>
            <label className="mb-1.5 block text-sm font-medium">{t('categories.bannerImage', 'Banner Image')}</label>
            {formData.banner ? (
              <div className="relative">
                <img
                  src={formData.banner.startsWith('data:') ? formData.banner : getCdnUrl(formData.banner, currentStore?._id, 'products', 500)}
                  alt=""
                  className="h-24 w-full rounded-lg object-cover border border-border-default"
                />
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, banner: undefined })}
                  className="absolute top-1.5 end-1.5 rounded-full bg-bg-primary/80 p-1 text-text-muted hover:text-danger"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => handleImageUpload('banner')}
                className="flex items-center gap-2 rounded-lg border border-dashed border-border-default px-4 py-2.5 text-sm text-text-muted hover:border-accent/50 hover:text-accent transition-colors w-full justify-center"
              >
                <Upload className="h-4 w-4" />
                {t('common.upload', 'Upload')}
              </button>
            )}
            <p className="mt-1.5 text-xs text-text-muted">{t('categories.bannerHint', 'Recommended: 1200 x 400 pixels')}</p>
          </div>

          <Toggle
            label={t('coupons.active')}
            checked={formData.isActive ?? true}
            onChange={(v) => setFormData({ ...formData, isActive: v })}
          />
        </div>
      </Modal>

      {/* Delete confirmation */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={t('common.confirm')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>{t('common.cancel')}</Button>
            <Button
              variant="danger"
              isLoading={deleteMutation.isPending}
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget._id)}
            >
              {t('common.delete')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-text-secondary">{t('categories.deleteConfirm')}</p>
        <p className="mt-2 font-medium">{deleteTarget?.name}</p>
      </Modal>

      {/* Hierarchy editor */}
      <HierarchyEditor
        isOpen={showHierarchyEditor}
        onClose={() => setShowHierarchyEditor(false)}
      />

      {/* Category stats modal */}
      {statsCategory && (
        <CategoryStatsModal
          isOpen={!!statsCategory}
          onClose={() => setStatsCategory(null)}
          categoryId={statsCategory._id}
          categoryName={statsCategory.name}
        />
      )}
    </div>
  );
}
